// Local dev uses the bundled signaling server; production has none deployed yet.
// Pointing a public HTTPS page at ws://localhost triggers Chrome's local network
// access prompt, so multiplayer stays off until wss://ws.pelaik.com exists (see server/HOSTING.md).
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]']
const IS_LOCAL = LOCAL_HOSTS.includes(window.location.hostname)
const SIGNALING_URL = IS_LOCAL ? 'ws://localhost:3001' : null

const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }]

export class Network {
    constructor({ onTileUpdate, onStatusChange, onReady } = {}) {
        this.onTileUpdate = onTileUpdate || (() => {})
        this.onStatusChange = onStatusChange || (() => {})
        this.onReady = onReady || (() => {})
        this.peers = new Map() // peerId -> { pc, channel }
        this.peerLocations = new Map() // peerId -> { lat, lng }
        this.myId = null
        this.myLocation = null
        this.ws = null
    }

    connect() {
        if (!SIGNALING_URL) {
            this.onStatusChange('single player — no signaling server')
            return
        }

        try {
            this.ws = new WebSocket(SIGNALING_URL)
        } catch {
            this.onStatusChange('offline — signaling server unreachable')
            return
        }

        this.ws.onopen = () => this.onStatusChange('signaling connected, waiting for peers...')
        this.ws.onclose = () => this.onStatusChange('disconnected from signaling server')
        this.ws.onerror = () => this.onStatusChange('offline — could not reach signaling server')

        this.ws.onmessage = ({ data }) => {
            try {
                this._handleSignal(JSON.parse(data))
            } catch (e) {
                console.error('bad signal message', e)
            }
        }
    }

    _handleSignal(msg) {
        switch (msg.type) {
            case 'welcome':
                this.myId = msg.id
                this.myLocation = msg.location
                this.onReady({ id: msg.id, location: msg.location })
                this._updateStatus()
                msg.peers.forEach(({ id, location }) => {
                    if (location) this.peerLocations.set(id, location)
                    this._initiateConnection(id)
                })
                break
            case 'offer':
                this._handleOffer(msg.from, msg.offer)
                break
            case 'answer':
                this._handleAnswer(msg.from, msg.answer)
                break
            case 'ice':
                this._handleIce(msg.from, msg.candidate)
                break
            case 'peer_left':
                this._removePeer(msg.id)
                this.peerLocations.delete(msg.id)
                this._updateStatus()
                break
        }
    }

    _createPC(peerId) {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })

        pc.onicecandidate = ({ candidate }) => {
            if (candidate) this._signal({ type: 'ice', to: peerId, candidate })
        }

        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
                this._removePeer(peerId)
                this._updateStatus()
            }
        }

        return pc
    }

    async _initiateConnection(peerId) {
        const pc = this._createPC(peerId)
        const channel = pc.createDataChannel('game', { ordered: false, maxRetransmits: 0 })
        this._setupChannel(peerId, channel)
        this.peers.set(peerId, { pc, channel })

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)
        this._signal({ type: 'offer', to: peerId, offer })
    }

    async _handleOffer(peerId, offer) {
        const pc = this._createPC(peerId)
        this.peers.set(peerId, { pc, channel: null })

        pc.ondatachannel = ({ channel }) => {
            this._setupChannel(peerId, channel)
            this.peers.get(peerId).channel = channel
        }

        await pc.setRemoteDescription(offer)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        this._signal({ type: 'answer', to: peerId, answer })
    }

    async _handleAnswer(peerId, answer) {
        const peer = this.peers.get(peerId)
        if (peer) await peer.pc.setRemoteDescription(answer)
    }

    async _handleIce(peerId, candidate) {
        const peer = this.peers.get(peerId)
        if (!peer) return
        try {
            await peer.pc.addIceCandidate(candidate)
        } catch (e) {
            console.warn('ICE candidate rejected', e)
        }
    }

    _setupChannel(peerId, channel) {
        channel.onopen = () => this._updateStatus()
        channel.onclose = () => this._updateStatus()
        channel.onmessage = ({ data }) => {
            try {
                const msg = JSON.parse(data)
                if (msg.type === 'tile_update') {
                    if (msg.location) this.peerLocations.set(peerId, msg.location)
                    this.onTileUpdate(msg.col, msg.row, msg.emoji, msg.location ?? null)
                }
            } catch (e) {
                console.error('bad peer message', e)
            }
        }
    }

    _removePeer(peerId) {
        const peer = this.peers.get(peerId)
        if (peer) {
            peer.pc.close()
            this.peers.delete(peerId)
        }
    }

    _signal(msg) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(msg))
        }
    }

    _updateStatus() {
        const connected = [...this.peers.values()].filter(
            p => p.channel?.readyState === 'open'
        ).length
        const peerMsg = connected === 0
            ? 'you are the only one here'
            : connected === 1
                ? '1 other person connected'
                : `${connected} other people connected`
        this.onStatusChange(`id: ${this.myId} | ${peerMsg}`)
    }

    getPeerLocations() {
        return [...this.peerLocations.values()]
    }

    broadcastTileUpdate(col, row, emoji) {
        const data = JSON.stringify({
            type: 'tile_update',
            col,
            row,
            emoji,
            location: this.myLocation
        })
        this.peers.forEach(({ channel }) => {
            if (channel?.readyState === 'open') channel.send(data)
        })
    }
}
